import type { CrewMember } from "../api/crewSchemas";
import { applyClockEvent } from "../domain/clock";
import type { BreakQualification, ClockEventKind, ClockState } from "../domain/clock";
import { parseInstant } from "../domain/time";
import type { EpochSeconds } from "../domain/time";
import type { LocalEvent } from "./model";
import { recordClockEvent } from "./recordEvent";
import type { RecordEventDependencies } from "./recordEvent";

/** Local clock state of a crew member (T-170). */
export interface CrewMemberClock {
  readonly state: ClockState;
  /** Instant of the last event, `null` if none is known. */
  readonly since: EpochSeconds | null;
  /** Events recorded for the person on this device that the server has not confirmed yet. */
  readonly pending: number;
}

/**
 * Returns the local clock state of a crew member (spec 8.1.2 for others): the state of the crew
 * loaded from the server plus the events recorded for the person on this device that the crew does not
 * reflect yet – pending ones and those confirmed after the crew was requested. An event the crew already
 * reflects (confirmed while the request was running) is not a valid step from its state and is skipped,
 * so nothing is counted twice; rejected events never count.
 *
 * @param member - The person as loaded with the crew (or a person signed in at a terminal, T-173).
 * @param fetchedAtMs - Device time when the crew was requested, in milliseconds.
 * @param events - Local events of the signed-in person (own events are ignored).
 * @returns The local state.
 */
export function crewMemberClock(
  member: Pick<CrewMember, "employeeId" | "state" | "since">,
  fetchedAtMs: number,
  events: readonly LocalEvent[],
): CrewMemberClock {
  let state: ClockState = member.state;
  let since: EpochSeconds | null = member.since === null ? null : parseInstant(member.since);
  let pending = 0;
  const unreflected = events.filter(
    (event) =>
      event.employeeId === member.employeeId &&
      (event.syncStatus === "pending" ||
        (event.syncStatus === "synced" && (event.syncedAtMs ?? 0) >= fetchedAtMs)),
  );

  for (const event of unreflected) {
    const next = applyClockEvent(state, event.kind);
    pending += event.syncStatus === "pending" ? 1 : 0;

    if (next.ok) {
      state = next.value;
      since = event.occurredAt;
    }
  }

  const clock: CrewMemberClock = { state, since, pending };
  return clock;
}

/** A crew member with the local state the step is checked against. */
export interface CrewTarget {
  readonly member: CrewMember;
  readonly state: ClockState;
}

/** Input of {@link recordCrewEvents}. */
export interface RecordCrewInput {
  readonly ownerSubject: string;
  readonly kind: ClockEventKind;
  /** Declared kind of a break start (ADR-0092); ignored for other events. */
  readonly breakQualification: BreakQualification | null;
  /** The chosen persons. */
  readonly targets: readonly CrewTarget[];
  /** Time zone for a person without employment zone (the recording person's). */
  readonly fallbackTimeZone: string;
  readonly source: LocalEvent["source"];
}

/** Result of {@link recordCrewEvents}. */
export interface RecordCrewResult {
  /** The events stored in the outbox, one per person whose state allowed the step. */
  readonly recorded: readonly LocalEvent[];
  /** The persons whose state does not allow the step; nothing is recorded for them. */
  readonly skipped: readonly CrewMember[];
}

/**
 * Records one step for each chosen person of a crew (T-170): one event per person whose local state
 * allows it, each with its own identifier, the person and the recording account, in the person's time
 * zone, stored in the outbox like an own event so that it synchronizes idempotently. A person whose state
 * does not allow the step is skipped; no further event is ever created on its own (`SINV-01`).
 *
 * @param dependencies - Store, identifier generator and clock.
 * @param input - The step and the chosen persons.
 * @returns The recorded events and the skipped persons.
 */
export async function recordCrewEvents(
  dependencies: RecordEventDependencies,
  input: RecordCrewInput,
): Promise<RecordCrewResult> {
  const recorded: LocalEvent[] = [];
  const skipped: CrewMember[] = [];

  for (const target of input.targets) {
    const result = await recordClockEvent(dependencies, {
      ownerSubject: input.ownerSubject,
      employeeId: target.member.employeeId,
      kind: input.kind,
      currentState: target.state,
      timeZoneId: target.member.timeZoneId ?? input.fallbackTimeZone,
      source: input.source,
      breakQualification: input.breakQualification,
    });

    if (result.ok) {
      recorded.push(result.value);
    } else {
      skipped.push(target.member);
    }
  }

  const outcome: RecordCrewResult = { recorded, skipped };
  return outcome;
}
